import { Autocomplete, Box, Button, Checkbox, Collapse, FormControlLabel, Grid, IconButton, MenuItem, OutlinedInput, Paper, PaperProps, Select, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ChangeEvent, forwardRef, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { SearchOutlined } from '@mui/icons-material'
import DateTimePickerField from '../../components/InputFields/DateTimePickerField'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { EMPTY_SEARCH_CRITERIA, EventsContext } from './EventsProvider'
import {
  EntitySuggestion,
  formatSuggestion,
  isSuggestableKind,
  SEARCH_TYPE_LABELS,
  SEARCH_TYPE_ORDER,
  SEARCH_TYPE_PLACEHOLDERS,
  SearchType,
} from '../../interfaces/IAffectedEntitySearch'

const SUGGEST_DEBOUNCE_MS = 250

function SearchBar() {
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    setSearchCriteria,
    searchCriteria,
    handleFilterChange,
    setIsSearchBarCollapsed,
    isSearchBarCollapsed,
    setShowSearchFilterButton,
    loadingEvents,
    setSearchedEvents,
    runSearch,
    fetchEntitySuggestions,
  } = useContext(EventsContext)

  const [suggestions, setSuggestions] = useState<EntitySuggestion[]>([])
  const [suggestLoading, setSuggestLoading] = useState(false)
  const [suggestHasMore, setSuggestHasMore] = useState(false)
  const latestSuggestRequestIdRef = useRef(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const [inputValue, setInputValue] = useState(searchCriteria.searchTerm)

  useEffect(() => {
    setInputValue(searchCriteria.searchTerm)
  }, [searchCriteria.searchTerm])

  useEffect(() => {
    const kind = searchCriteria.searchType
    if (!isSuggestableKind(kind)) {
      setSuggestions([])
      setSuggestHasMore(false)
      setSuggestLoading(false)
      return
    }

    const term = inputValue.trim()
    if (term.length < 3) {
      setSuggestions([])
      setSuggestHasMore(false)
      setSuggestLoading(false)
      return
    }

    const requestId = latestSuggestRequestIdRef.current + 1
    latestSuggestRequestIdRef.current = requestId
    setSuggestLoading(true)

    const handle = window.setTimeout(async () => {
      try {
        const result = await fetchEntitySuggestions(kind, term)
        if (latestSuggestRequestIdRef.current === requestId) {
          setSuggestions(result.values)
          setSuggestHasMore(result.hasMore)
        }
      } finally {
        if (latestSuggestRequestIdRef.current === requestId) {
          setSuggestLoading(false)
        }
      }
    }, SUGGEST_DEBOUNCE_MS)

    return () => window.clearTimeout(handle)
  }, [searchCriteria.searchType, inputValue, fetchEntitySuggestions])

  const suggestHasMoreRef = useRef(suggestHasMore)
  suggestHasMoreRef.current = suggestHasMore
  const SuggestPaper = useMemo(() => {
    const Component = forwardRef<HTMLDivElement, PaperProps>((props, ref) => (
      <Paper {...props} ref={ref}>
        {props.children}
        {suggestHasMoreRef.current ? (
          <Box
            sx={{
              padding: 1,
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: '0.75rem',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            20+ treff – skriv flere tegn for å snevre inn
          </Box>
        ) : null}
      </Paper>
    ))
    Component.displayName = 'SuggestPaper'
    return Component
  }, [])

  const handleResetSubmit = async () => {
    setIsSearchBarCollapsed(false)
    setShowSearchFilterButton(false)
    setSearchedEvents([])
    setSearchCriteria(EMPTY_SEARCH_CRITERIA)
    handleFilterChange('daily')
  }

  const handleSubmit = async () => {
    // If the user typed a customer name and hit Enter without picking from the dropdown, the raw
    // text won't match the by-entity backend (which looks up by CustomerNumber). Promote the first
    // suggestion so name-typed submits still resolve to a numeric lookup.
    let term = inputValue
    if (
      searchCriteria.searchType === 'CustomerNumber'
      && !term.includes(' — ')
      && suggestions.length > 0
    ) {
      term = formatSuggestion(suggestions[0])
      setInputValue(term)
    }
    if (!term.trim() && searchCriteria.searchType !== 'FreeText') return
    const committed = { ...searchCriteria, searchTerm: term }
    setSearchCriteria(committed)
    await runSearch(committed)
  }

  const placeholder = SEARCH_TYPE_PLACEHOLDERS[searchCriteria.searchType]
  const showAutocomplete = isSuggestableKind(searchCriteria.searchType)
  const outerBottomCorner = isSearchBarCollapsed ? '0px' : '8px'

  const endAdornment = (
    <>
      <Box
        sx={{
          paddingRight: 1,
          borderRight: `2px dashed ${theme.palette.divider}`,
        }}
      >
        <IconButton disabled={loadingEvents} size="small" onClick={() => setIsSearchBarCollapsed(!isSearchBarCollapsed)}>
          {isSearchBarCollapsed ? (
            <KeyboardArrowUpOutlinedIcon sx={{ color: 'primary.main' }} />
          ) : (
            <KeyboardArrowDownOutlinedIcon sx={{ color: 'primary.main' }} />
          )}
        </IconButton>
      </Box>
      <Box sx={{ paddingLeft: 1 }}>
        <Stack direction={'row'}>
          <IconButton disabled={loadingEvents} size="small" onClick={handleResetSubmit} title="Nullstill søk">
            <RestartAltIcon sx={{ color: 'primary.main' }} />
          </IconButton>
          <IconButton disabled={loadingEvents} size="small" onClick={handleSubmit}>
            <SearchOutlined sx={{ color: 'primary.main' }} />
          </IconButton>
        </Stack>
      </Box>
    </>
  )

  return (
    <Box sx={{
      flex: 'auto'
    }}>
      <Stack direction={isXs ? 'column' : 'row'} spacing={0} sx={{ alignItems: 'stretch' }}>
        <Select
          size="small"
          disabled={loadingEvents}
          value={searchCriteria.searchType}
          onChange={(e) => {
            setSearchCriteria({
              ...searchCriteria,
              searchType: e.target.value as SearchType,
            })
            setTimeout(() => inputRef.current?.focus(), 0)
          }}
          sx={{
            minWidth: isXs ? 0 : 170,
            width: isXs ? '100%' : 'auto',
            backgroundColor: 'background.paper',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: isXs ? '8px' : 0,
            borderBottomLeftRadius: isXs ? 0 : outerBottomCorner,
            borderBottomRightRadius: 0,
            '& fieldset': isXs ? { borderBottom: 0 } : { borderRight: 0 },
          }}
        >
          {SEARCH_TYPE_ORDER.map((t) => (
            <MenuItem key={t} value={t}>
              {SEARCH_TYPE_LABELS[t]}
            </MenuItem>
          ))}
        </Select>
        {showAutocomplete ? (
          <Autocomplete<EntitySuggestion, false, true, true>
            freeSolo
            disableClearable
            disabled={loadingEvents}
            filterOptions={(opts) => opts}
            options={suggestions}
            loading={suggestLoading}
            slots={{ paper: SuggestPaper }}
            value={inputValue}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : formatSuggestion(opt))}
            isOptionEqualToValue={(option, value) =>
              typeof value === 'string' ? formatSuggestion(option) === value : option.value === value.value
            }
            renderOption={(props, opt) => {
              const label = typeof opt === 'string' ? opt : formatSuggestion(opt)
              const key = typeof opt === 'string' ? opt : opt.value
              return (
                <li {...props} key={key}>
                  {label}
                </li>
              )
            }}
            onChange={(_event, value) => {
              if (typeof value === 'string') {
                setInputValue(value)
              } else if (value) {
                setInputValue(formatSuggestion(value))
              } else {
                setInputValue('')
              }
            }}
            onInputChange={(_event, value, reason) => {
              if (reason === 'reset') return
              const capped = value.length > 100 ? value.slice(0, 100) : value
              setInputValue(capped)
            }}
            sx={{
              flex: 'auto',
              backgroundColor: 'background.paper',
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={inputRef}
                placeholder={placeholder}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit()
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingRight: '14px',
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: isXs ? 0 : undefined,
                    borderBottomLeftRadius: isXs ? outerBottomCorner : 0,
                    borderBottomRightRadius: outerBottomCorner,
                  },
                }}
                slotProps={{
                  ...params.slotProps,
                  htmlInput: {
                    ...params.slotProps.htmlInput,
                    autoComplete: 'one-time-code',
                    name: 'event-search-q',
                  },
                  input: {
                    ...params.slotProps.input,
                    endAdornment,
                  },
                }}
              />
            )}
          />
        ) : (
          <OutlinedInput
            disabled={loadingEvents}
            color="primary"
            fullWidth
            inputRef={inputRef}
            placeholder={placeholder}
            sx={{
              backgroundColor: 'background.paper',
              borderTopLeftRadius: 0,
              borderTopRightRadius: isXs ? 0 : '8px',
              borderBottomLeftRadius: isXs ? outerBottomCorner : 0,
              borderBottomRightRadius: outerBottomCorner,
            }}
            value={inputValue}
            inputProps={{
              maxLength: 100,
              autoComplete: 'one-time-code',
              name: 'event-search-q',
              sx: {
                fontSize: 'body2.fontSize',
              },
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setInputValue(event.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit()
              }
            }}
            endAdornment={endAdornment}
          />
        )}
      </Stack>
      <Collapse in={isSearchBarCollapsed} sx={{ flex: 'auto' }}>
        <Box
          sx={{
            padding: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderTop: 'none',
            flex: 'auto',
            backgroundColor: 'background.paper',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px'
          }}>
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <DateTimePickerField
                title="Fra dato"
                name="fromDate"
                value={searchCriteria.fromDate}
                onChange={(e: any) => {
                  setSearchCriteria({ ...searchCriteria, fromDate: new Date(e) })
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <DateTimePickerField
                title="Til dato"
                name="toDate"
                value={searchCriteria.toDate}
                onChange={(e: any) => {
                  setSearchCriteria({ ...searchCriteria, toDate: new Date(e) })
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={searchCriteria.isOnlyActive}
                    onChange={(e: any) => setSearchCriteria({ ...searchCriteria, isOnlyActive: e.target.checked })}
                    onClick={(e) => e.stopPropagation()}
                  />
                }
                label={<Typography variant="body2">Kun aktive</Typography>}
              />
            </Grid>
            <Grid size={12}>
              <Stack spacing={1} direction={'row'} sx={{
                justifyContent: 'right'
              }}>
                <Button variant="contained" color="info" size="small" onClick={handleResetSubmit}>
                  Nullstill
                </Button>
                <Button variant="contained" size="small" onClick={handleSubmit}>
                  Søk
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}

export default SearchBar
